import { Component, inject } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import {
    GrdfAccessRightModel,
    GrdfAccessRightProofType,
    GrdfContractualDataDefinition,
    GrdfContractualDataModel,
    GrdfTechnicalDataDefinition,
    GrdfTechnicalDataModel,
} from '../../../grdf.models';
import { filter, Observable } from 'rxjs';
import { GrdfState, UpdateFileProof } from '../../../grdf.state';
import { LegendConfig } from 'src/app/shared/components/map/map.component';
import { DefaultPolygonStyle, MapColorsAlt } from 'src/app/shared/components/map/map.constants';
import { PathOptions } from 'leaflet';
import { RestService } from 'src/app/core/services/rest.service';
import { AlertService, ButtonColors } from '@ed---interne/ng-uui-components';
import { GrdfService } from '../../../grdf.service';
import { HttpResponse } from '@angular/common/http';
import { User } from 'src/app/core/models/user.model';
import { AuthState } from 'src/app/core/states/auth.state';

@UntilDestroy()
@Component({
    selector: 'app-grdf-meter-general',
    templateUrl: './grdf-meter-general.component.html',
    styleUrls: ['./grdf-meter-general.component.scss'],
})
export class GrdfMeterGeneralComponent {
    grdfTechnicalDataDefinition = GrdfTechnicalDataDefinition;
    grdfContractualDataDefinition = GrdfContractualDataDefinition;

    public isProofFile = false;

    public loggedUser$: Observable<User | null> = inject(Store).select(AuthState.loggedUser);
    public loggedUser: User | undefined;

    public grdfAccessRight$: Observable<GrdfAccessRightModel> = inject(Store)
        .select(GrdfState.currentAccessRight)
        .pipe(filter(Boolean));
    public accessRight: GrdfAccessRightModel | null = null;

    public grdfTechnicalData$: Observable<GrdfTechnicalDataModel> = inject(Store)
        .select(GrdfState.currentAccessRightTechnicalData)
        .pipe(filter(Boolean));
    public technicalData: GrdfTechnicalDataModel | null = null;

    public grdfContractualData$: Observable<GrdfContractualDataModel> = inject(Store)
        .select(GrdfState.currentAccessRightContractualData)
        .pipe(filter(Boolean));
    public contractualData: GrdfContractualDataModel | null = null;

    public geometries: any[] | null = null;
    public legendConfig: LegendConfig[] = [
        {
            title: 'Localisation',
            values: [
                {
                    color: MapColorsAlt.paleGreen,
                    value: 'Position la plus probable',
                },
            ],
        },
    ];

    buttonColorsPrimary = ButtonColors.Primary;
    constructor(
        private readonly store: Store,
        private readonly restService: RestService,
        private readonly alertService: AlertService,
        private readonly grdfService: GrdfService,
    ) {
        this.loggedUser$.pipe(untilDestroyed(this)).subscribe((data: any) => {
            this.loggedUser = data;
        });

        this.grdfAccessRight$.pipe(untilDestroyed(this)).subscribe((grdfAccessRight) => {
            this.accessRight = grdfAccessRight;
            this.isProofFile = grdfAccessRight.proofType == GrdfAccessRightProofType.FILE;
        });

        this.grdfTechnicalData$.pipe(untilDestroyed(this)).subscribe((technicalData) => {
            console.log(
                `grdfTechnicalData changed, reload localization to ${this.technicalData?.numero_rue} ${this.technicalData?.nom_rue} - ${this.technicalData?.code_postal}`,
            );
            this.technicalData = technicalData;
            this.getLocalization();
        });

        this.grdfContractualData$.pipe(untilDestroyed(this)).subscribe((contractualData) => {
            contractualData.date_debut_profil_type_actuel = new Date(
                contractualData.date_debut_profil_type_actuel,
            );
            contractualData.date_fin_profil_type_actuel = new Date(
                contractualData.date_fin_profil_type_actuel,
            );
            contractualData.date_mes = new Date(contractualData.date_mes);
            contractualData.date_publication = new Date(contractualData.date_publication);
            this.contractualData = contractualData;
        });
    }

    async getLocalization() {
        let geo: any;
        if (this.technicalData?.nom_rue) {
            geo = await this.restService.geocode(
                `${this.technicalData?.numero_rue} ${this.technicalData?.nom_rue} `,
                this.technicalData?.code_postal,
            );
            this.geometries = [geo.features[0]];
        } else {
            console.log('no numeroEtNomVoie');
        }
    }
    public getMapStyle(feature: GeoJSON.Feature | undefined): PathOptions {
        let fillColor = MapColorsAlt.paleGreen;

        return {
            ...DefaultPolygonStyle,
            fillColor,
        };
    }

    public getTooltip(feature: GeoJSON.Feature): string {
        return `${feature?.properties?.['properties'].label}`;
    }

    public toNumber(string: string | undefined) {
        return Number(string);
    }

    public getFooterTarifAcheminement(tarif_acheminement: string | undefined) {
        if (tarif_acheminement == 'T1') return ' < 6 000 kWh/an';
        else if (tarif_acheminement == 'T2') return 'Entre 6 et 300 MWh/an';
        else if (tarif_acheminement == 'T3') return 'Entre 0,3 et 5 GWh/an';
        else if (tarif_acheminement == 'T4') return '> 5 GWh/an';
        else if (tarif_acheminement == 'TP') return 'tarif de proximité';
        else if (tarif_acheminement == 'TB') return 'Tarif biométhane';
        else return ' ';
    }

    public downloadProofFile(type: 'userToThirdParty' | 'userToConsumer'): void {
        if (!this.accessRight) return;
        try {
            this.grdfService
                .getFileProof(this.accessRight.id, type)
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (response: HttpResponse<Blob>) => {
                        const a = document.createElement('a');
                        const objectUrl = URL.createObjectURL(response.body!);
                        a.href = objectUrl;
                        const contentDisposition =
                            response.headers.get('Content-Disposition') || '';
                        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
                        const fileName =
                            fileNameMatch?.[1] ||
                            `consentement-${this.accessRight?.id}-${this.accessRight?.pdlNumber}`;

                        a.download = fileName;
                        a.click();
                        URL.revokeObjectURL(objectUrl);
                    },
                    error: (error) => {
                        console.log(error);
                        this.alertService.error(
                            'Erreur lors du téléchargement',
                            `${error?.message}`,
                        );
                    },
                });
        } catch (error: any) {
            console.log(error);
            this.alertService.error('Erreur lors du téléchargement', `${error?.message}`);
        }
    }

    public onFileUpload(file: File) {
        if (!this.accessRight) return;
        this.store
            .dispatch(new UpdateFileProof({ accessRightId: this.accessRight.id, fileProof: file }))
            .subscribe({
                next: () => {
                    this.alertService.valid(
                        'Modification du fichier de consentement',
                        'le fichier à bien été enregistré',
                    );
                },
                error: (err) => {
                    console.info(err);
                    this.alertService.error(
                        'Modification du fichier de consentement',
                        err?.error ?? 'Une erreur est survenue',
                    );
                },
            });
    }
}
